import URI from 'jsuri';

import API from 'js/lib/api';
import store from 'js/lib/coursera.store';

import { DEFAULT_PROTOCOL } from 'bundles/widget-simulator/constants';
import type {
  GetOptionsStateFn,
  OptionProps,
  OptionsState,
  SimulatorSession,
  SimulatorSessionMutator,
  SubmitForGradingResponse,
} from 'bundles/widget-simulator/types';
import uuid from 'bundles/widget-simulator/utils/uuid';

export const widgetGradingExecutionsV1 = API('/api/widgetGradingExecutions.v1', { type: 'rest' });

// DO NOT CHANGE THIS VALUE. Plugin simulator users (plugin authors) will lose access to their state.
export const LOCAL_STORAGE_KEY = 'coursera-plugin-simulator-session';

const makeOptionState = (): OptionsState => ({
  selectedIndex: -1,
  options: [],
});

export const write = (session: SimulatorSession) => {
  store.set(LOCAL_STORAGE_KEY, JSON.stringify(session));
};

export const defaultSimulatorSession = {
  url: undefined,
  protocol: DEFAULT_PROTOCOL,
  isServerSideGradingEnabled: undefined,
  scope: {
    course: makeOptionState(),
    plugin: makeOptionState(),
  },
  sessionConfig: makeOptionState(),
  graderConfig: makeOptionState(),
};

/**
 * Reads the simulator session data from localStorage. If none exists, this will
 * initialize it.
 */
export const read = (): SimulatorSession => {
  if (!store.get(LOCAL_STORAGE_KEY)) {
    write(defaultSimulatorSession);
  }

  return JSON.parse(store.get(LOCAL_STORAGE_KEY) || '');
};

export const loadSimulatorSession = (): Promise<SimulatorSession> => {
  return new Promise((resolve) => resolve(read()));
};

/**
 * The entire simulator session (a relatively small object) is read fresh from
 * localStorage each time it is access and updated. This is to make sure we always
 * have the most up-to-date value regardless of when or where or how the underlying
 * value is updated (for example, by hand).
 *
 * This provides an abstraction around reading the session, editing it, and then
 * saving it back again.
 */
export const mutate = async (fn: SimulatorSessionMutator): Promise<SimulatorSession> => {
  return loadSimulatorSession().then((session: SimulatorSession): SimulatorSession => {
    fn(session);
    write(session);
    return session;
  });
};

export const setProtocol = async (protocol: string | undefined): Promise<SimulatorSession> => {
  return mutate((session: SimulatorSession): void => {
    // eslint-disable-next-line no-param-reassign
    session.protocol = protocol;
  });
};

export const setUrl = async (url: string | undefined): Promise<SimulatorSession> => {
  return mutate((session: SimulatorSession): void => {
    // eslint-disable-next-line no-param-reassign
    session.url = url;
  });
};

export const toggleServerSideGrading = async (): Promise<SimulatorSession> => {
  return mutate((session: SimulatorSession): void => {
    // eslint-disable-next-line no-param-reassign
    session.isServerSideGradingEnabled = !session.isServerSideGradingEnabled;
  });
};

export const submitForGrading = async (
  graderScript: string,
  graderConfiguration: string,
  sessionConfiguration: string,
  answer: string
): Promise<SubmitForGradingResponse> => {
  const uri = new URI().addQueryParam('action', 'gradeSimulatorRequest');
  const data = {
    typeName: 'lambdaScript',
    definition: {
      script: graderScript,
      graderConfiguration,
      sessionConfiguration,
      answer,
    },
  };

  return widgetGradingExecutionsV1
    .post(uri.toString(), { data })
    .then((response: SubmitForGradingResponse): SubmitForGradingResponse => {
      if (response.errorCode) {
        return {
          ...response,
          failure: {
            message: `An unknown error has occurred: ${response.errorCode}`,
          },
        };
      }

      return response;
    });
};

// TODO: All of this option code could be DRYed up a lot.

const createSetSelectedOptionFunction = (getOptionsStateFn: GetOptionsStateFn) => {
  return (selectedIndex: number): Promise<SimulatorSession> => {
    return mutate((session: SimulatorSession): void => {
      const state = getOptionsStateFn(session);
      state.selectedIndex = selectedIndex;
    });
  };
};

const createAddOptionFunction = (getOptionsStateFn: GetOptionsStateFn) => {
  return (props: OptionProps): Promise<SimulatorSession> => {
    return mutate((session: SimulatorSession): void => {
      const state = getOptionsStateFn(session);

      state.options.push({
        id: uuid(),
        ...props,
      });
      state.selectedIndex = state.options.length - 1;
    });
  };
};

const createUpdateOptionFunction = (getOptionsStateFn: GetOptionsStateFn) => {
  return (id: string, newProps: OptionProps): Promise<SimulatorSession> => {
    return mutate((session: SimulatorSession): void => {
      const state = getOptionsStateFn(session);
      const index = state.options.findIndex((plugin) => plugin.id === id);

      if (index !== -1) {
        // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        state.options[index] = { ...state.options[index], ...newProps };
      }
    });
  };
};

const createRemoveOptionFunction = (getOptionsStateFn: GetOptionsStateFn) => {
  return (index: number): Promise<SimulatorSession> => {
    return mutate((session: SimulatorSession): void => {
      const state = getOptionsStateFn(session);
      state.options.splice(index, 1);

      if (state.options.length === 0) {
        state.selectedIndex = -1;
      } else if (state.selectedIndex === index) {
        state.selectedIndex = 0;
      }
    });
  };
};

// Course scope options.
const courseScopeOptionStateFn = (session: SimulatorSession) => session.scope.course;
export const setSelectedCourseScope = createSetSelectedOptionFunction(courseScopeOptionStateFn);
export const addCourseScope = createAddOptionFunction(courseScopeOptionStateFn);
export const updateCourseScope = createUpdateOptionFunction(courseScopeOptionStateFn);
export const removeCourseScope = createRemoveOptionFunction(courseScopeOptionStateFn);

// Plugin scope options.
const pluginScopeOptionStateFn = (session: SimulatorSession) => session.scope.plugin;
export const setSelectedPluginScope = createSetSelectedOptionFunction(pluginScopeOptionStateFn);
export const addPluginScope = createAddOptionFunction(pluginScopeOptionStateFn);
export const updatePluginScope = createUpdateOptionFunction(pluginScopeOptionStateFn);
export const removePluginScope = createRemoveOptionFunction(pluginScopeOptionStateFn);

// Session config options.
const sessionConfigOptionStateFn = (session: SimulatorSession) => session.sessionConfig;
export const setSelectedSessionConfig = createSetSelectedOptionFunction(sessionConfigOptionStateFn);
export const addSessionConfig = createAddOptionFunction(sessionConfigOptionStateFn);
export const updateSessionConfig = createUpdateOptionFunction(sessionConfigOptionStateFn);
export const removeSessionConfig = createRemoveOptionFunction(sessionConfigOptionStateFn);

// Grader config options.
const graderConfigOptionStateFn = (session: SimulatorSession) => session.graderConfig;
export const setSelectedGraderConfig = createSetSelectedOptionFunction(graderConfigOptionStateFn);
export const addGraderConfig = createAddOptionFunction(graderConfigOptionStateFn);
export const updateGraderConfig = createUpdateOptionFunction(graderConfigOptionStateFn);
export const removeGraderConfig = createRemoveOptionFunction(graderConfigOptionStateFn);
